// Datos de las cuatro curvas de 056A/056B para el PDF firmado (`DatosCurvasCrecimientoPdf` de @logiacare/hce-pdf): la referencia de cada curva
// sobre 10 a 19 años más las mediciones del paciente (aunque sea una sola, que la curva de pantalla exige de a dos). Puro, sin React.
import type { Observation, Patient } from '@medplum/fhirtypes';
import type { DatosCurvasCrecimientoPdf, GraficaCurvaPdf, PuntoCurvaPdf, SerieCurvaPdf } from '@logiacare/hce-pdf';
import { edadExactaEnDias } from '../lib/percentilesCrecimiento';
import type { PuntoSerie, Serie } from '../graficos/tipos';
import { lineasReferenciaAdolescente } from './curvaPercentilPorEdad';
import { lineasReferenciaVelocidadTalla, puntosVelocidadTalla } from './velocidadCrecimiento';

const PESO_LOINC = '29463-7';
const TALLA_LOINC = '8302-2';
const DIAS_POR_ANIO = 365.25;
const DESDE_ANIOS = 10;
const HASTA_ANIOS = 19;

export interface TextosCurvasPdf {
  readonly peso: { readonly titulo: string; readonly nota: string };
  readonly talla: { readonly titulo: string; readonly nota: string };
  readonly imc: { readonly titulo: string; readonly nota: string };
  readonly velocidad: { readonly titulo: string; readonly nota: string };
  readonly ejeX: string;
}

/** El servidor rechaza más de 80 puntos por serie: del paciente se conservan los últimos (los más recientes). */
const TOPE_PUNTOS_PACIENTE = 80;

const anios = (dias: number): number => Math.round((dias / DIAS_POR_ANIO) * 100) / 100;
const redondeo = (n: number): number => Math.round(n * 100) / 100;

/** Serie de referencia (x en días → años, y redondeado); las líneas con menos de 2 puntos no se dibujan. */
function aSerieReferencia(s: Serie, estilo: SerieCurvaPdf['estilo'] = 'referencia'): SerieCurvaPdf | undefined {
  const puntos: PuntoCurvaPdf[] = s.puntos.map((p) => ({ x: anios(p.x), y: redondeo(p.y) }));
  return puntos.length >= 2 ? { etiqueta: s.etiqueta, estilo, puntos } : undefined;
}

interface PuntoFechado extends PuntoSerie {
  readonly fecha: string;
}

function puntosDe(observaciones: readonly Observation[], loinc: string, nacimiento: string): PuntoFechado[] {
  return observaciones
    .filter((o) => o.status !== 'entered-in-error' && o.code?.coding?.some((c) => c.system === 'http://loinc.org' && c.code === loinc))
    .filter((o): o is Observation & { effectiveDateTime: string; valueQuantity: { value: number } } => typeof o.effectiveDateTime === 'string' && typeof o.valueQuantity?.value === 'number')
    .map((o) => ({ x: edadExactaEnDias(nacimiento, o.effectiveDateTime) / DIAS_POR_ANIO, y: o.valueQuantity.value, fecha: o.effectiveDateTime }))
    .filter((p) => p.x >= DESDE_ANIOS && p.x <= HASTA_ANIOS)
    .sort((a, b) => a.x - b.x);
}

/** Ejes Y "redondos" (múltiplos de `paso`) que abarquen toda la referencia y las mediciones. */
function rangoY(series: readonly SerieCurvaPdf[], paso: number): { yMin: number; yMax: number } {
  const ys = series.flatMap((s) => s.puntos.map((p) => p.y));
  if (ys.length === 0) return { yMin: 0, yMax: paso };
  return { yMin: Math.max(0, Math.floor(Math.min(...ys) / paso) * paso), yMax: Math.ceil(Math.max(...ys) / paso) * paso || paso };
}

function grafica(titulo: string, nota: string, ejeX: string, ejeY: string, series: readonly SerieCurvaPdf[], paso: number): GraficaCurvaPdf {
  const { yMin, yMax } = rangoY(series, paso);
  return { titulo, nota, ejeXEtiqueta: ejeX, ejeYEtiqueta: ejeY, xMin: DESDE_ANIOS, xMax: HASTA_ANIOS, yMin, yMax, series };
}

/**
 * Las cuatro gráficas de la lámina con su referencia (Peso/Edad NCHS/2000, Talla/Edad e IMC/Edad OMS 2007, Velocidad de Orbegozo) y las mediciones del
 * paciente; `undefined` si falta la fecha de nacimiento o el sexo (sin ellos no hay eje de edad ni referencia). Mediciones marcadas
 * `entered-in-error` no cuentan.
 */
export function datosCurvasAdolescentePdf(paciente: Pick<Patient, 'birthDate' | 'gender'>, observaciones: readonly Observation[], textos: TextosCurvasPdf): DatosCurvasCrecimientoPdf | undefined {
  if (!paciente.birthDate || (paciente.gender !== 'male' && paciente.gender !== 'female')) return undefined;
  const nacimiento = paciente.birthDate;
  const referencia = (tipo: 'peso' | 'talla' | 'imc'): SerieCurvaPdf[] => lineasReferenciaAdolescente(paciente.gender, tipo).map((s) => aSerieReferencia(s)).filter((s): s is SerieCurvaPdf => s !== undefined);
  const pesoP = puntosDe(observaciones, PESO_LOINC, nacimiento);
  const tallaP = puntosDe(observaciones, TALLA_LOINC, nacimiento);
  // IMC: pares peso/talla del MISMO instante, como en pantalla.
  const tallaPorFecha = new Map(tallaP.map((p) => [p.fecha, p.y]));
  const imcP = pesoP.flatMap((p) => {
    const cm = tallaPorFecha.get(p.fecha);
    return cm && cm > 0 ? [{ x: p.x, y: p.y / ((cm / 100) * (cm / 100)) }] : [];
  });
  const delPaciente = (etiqueta: string, puntos: readonly PuntoSerie[]): SerieCurvaPdf[] => (puntos.length > 0 ? [{ etiqueta, estilo: 'paciente', puntos: puntos.slice(-TOPE_PUNTOS_PACIENTE).map((p) => ({ x: redondeo(p.x), y: redondeo(p.y) })) }] : []);

  const velocidadPuntos = puntosVelocidadTalla(observaciones.filter((o) => o.status !== 'entered-in-error'), nacimiento)
    .map((p) => ({ x: p.x / DIAS_POR_ANIO, y: p.y }))
    .filter((p) => p.x >= DESDE_ANIOS && p.x <= HASTA_ANIOS);
  const referenciaVelocidad = lineasReferenciaVelocidadTalla(paciente.gender)
    .map((s) => aSerieReferencia(s, ['P10', 'P75', 'P90'].includes(s.etiqueta) ? 'referenciaDiscontinua' : 'referencia'))
    .filter((s): s is SerieCurvaPdf => s !== undefined);

  return {
    graficas: [
      grafica(textos.peso.titulo, textos.peso.nota, textos.ejeX, 'kg', [...referencia('peso'), ...delPaciente('Peso', pesoP)], 10),
      grafica(textos.talla.titulo, textos.talla.nota, textos.ejeX, 'cm', [...referencia('talla'), ...delPaciente('Talla', tallaP)], 10),
      grafica(textos.imc.titulo, textos.imc.nota, textos.ejeX, 'kg/m²', [...referencia('imc'), ...delPaciente('IMC', imcP)], 2),
      grafica(textos.velocidad.titulo, textos.velocidad.nota, textos.ejeX, 'cm/año', [...referenciaVelocidad, ...delPaciente('Velocidad', velocidadPuntos)], 2),
    ],
  };
}
